import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from './themeAugmentation';
import { createTheme } from '@mui/material/styles';
import { readableColor } from 'polished';
import arrowExpandIcon from '../assets/icons/arrow-expand-combo.svg';
import React from 'react';
import { IconButton } from '@mui/material';
import calendarIcon from '../assets/icons/icon-calendar.svg';
type Color = string;
type Options = {
  text?: Color;
  priceColor?: Color;
  primary?: Color;
  primaryReadable?: Color;
  secondary?: Color;
  secondaryReadable?: Color;
  success?: Color;
  danger?: Color;
  warning?: Color;
  info?: Color;
  gray0?: Color;
  gray1?: Color;
  gray2?: Color;
  gray3?: Color;
  gray4?: Color;
  gray5?: Color;
  gray6?: Color;
  fontFamily?: string;
};
export function create({
  text = '#273240',
  priceColor = '#008009',
  primary = '#FEC437',
  primaryReadable = '#fff',
  secondary = '#fff',
  secondaryReadable = '#273240',
  success = '#4BAF4F',
  danger = '#FF0000',
  warning = '#FDBA01',
  info = '#0C83D9',
  gray0 = '#A8A8A8',
  gray1 = '#F5F5F5',
  gray2 = '#EDEDED',
  gray3 = '#DFDFDF',
  gray4 = '#C5C5C5',
  gray5 = '#9C9C9C',
  gray6 = '#616161',
  fontFamily = '"Manrope", sans-serif'
}: Options = {}) {
  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 769,
        md: 1088,
        lg: 1366,
        xl: 2560
      }
    },
    spacing: 10,
    palette: {
      primary: {
        main: primary,
        contrastText: primaryReadable
      },
      secondary: {
        main: secondary,
        contrastText: secondaryReadable
      },
      success: {
        main: success,
        contrastText: '#ffffff'
      },
      error: {
        main: danger,
        contrastText: '#ffffff'
      },
      warning: {
        main: warning,
        contrastText: '#ffffff'
      },
      info: {
        main: info,
        contrastText: '#ffffff'
      },
      textColor: {
        main: text,
        contrastText: '#ffffff'
      },
      priceColor: {
        main: priceColor,
        contrastText: readableColor(priceColor)
      },
      gray0: {
        main: gray0,
        contrastText: readableColor(gray0)
      },
      gray1: {
        main: gray1,
        contrastText: readableColor(gray1)
      },
      gray2: {
        main: gray2,
        contrastText: readableColor(gray2)
      },
      gray3: {
        main: gray3,
        contrastText: readableColor(gray3)
      },
      gray4: {
        main: gray4,
        contrastText: readableColor(gray4)
      },
      gray5: {
        main: gray5,
        contrastText: readableColor(gray5)
      },
      gray6: {
        main: gray6,
        contrastText: readableColor(gray6)
      }
    },
    typography: {
      fontFamily,
      fontSize: 14,
      button: {
        fontFamily
      }
    },
    shape: {
      borderRadius: 2
    },
    components: {
      MuiFormControl: {
        styleOverrides: {
          root: {
            minWidth: '90px'
          }
        }
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            fontSize: '14px',
            '&.NiaraCheckboxLabel .MuiFormControlLabel-label': {
              fontWeight: 'bold',
              fontSize: '12px',
              textAlign: 'left'
            }
          },
          label: {
            fontSize: '14px'
          }
        }
      },
      MuiInputLabel: {
        defaultProps: {
          color: 'textColor'
        },
        styleOverrides: {
          root: {
            fontSize: '14px',
            fontWeight: 'bold',
            transform: 'translate(16px, 15px) scale(1)'
          },
          shrink: ({
            ownerState
          }) => ({
            transform: ownerState.size === 'small' ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, -9px) scale(0.857142)'
          }),
          sizeSmall: {
            transform: 'translate(16px, 7px) scale(1)'
          },
          asterisk: {
            color: '#FF0000'
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          inputMultiline: {
            padding: '0'
          },
          input: {
            padding: '14.5px 16px',
            fontSize: '14px',
            fontWeight: 600,
            height: '48px',
            boxSizing: 'border-box'
          },
          sizeSmall: {
            padding: '10px 14px',
            fontSize: '14px',
            fontWeight: 600,
            height: '36px',
            boxSizing: 'border-box'
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '14.5px 16px',
            fontSize: '14px',
            fontWeight: 600,
            height: '48px',
            boxSizing: 'border-box'
          }
        },
        defaultProps: {
          IconComponent: props => <img {...props} src={arrowExpandIcon} />
        }
      },
      MuiOutlinedInput: {
        defaultProps: {
          color: 'textColor'
        },
        styleOverrides: {
          notchedOutline: {
            fontWeight: 600,
            borderColor: '#DFDFDF'
          },
          root: {
            backgroundColor: '#FFFFFF',
            '&.Mui-disabled': {
              backgroundColor: '#E6E6E6'
            }
          },
          sizeSmall: {
            height: '36px',
            padding: '10px 0px'
          }
        }
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            padding: '0',
            paddingLeft: '16px',
            height: '48px',
            boxSizing: 'border-box'
          },
          root: {
            '.MuiOutlinedInput-root': {
              padding: '0',
              input: {
                padding: '16.5px 14px !important'
              }
            }
          }
        },
        defaultProps: {
          popupIcon: <img src={arrowExpandIcon} />
        }
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            padding: '4px 10px',
            borderRadius: '4px',
            height: '40px',
            maxHeight: '40px',
            minWidth: '40px',
            fontSize: '14px',
            lineHeight: 1
          },
          sizeMedium: {
            padding: '4px 10px',
            height: '40px',
            maxHeight: '40px',
            minWidth: '40px',
            fontSize: '14px'
          },
          sizeSmall: {
            padding: '4px 10px',
            height: '36px',
            maxHeight: '36px',
            minWidth: '36px',
            fontSize: '12px'
          },
          sizeLarge: {
            padding: '4px 14px',
            height: '48px',
            maxHeight: '48px',
            minWidth: '48px',
            fontSize: '14px'
          },
          sizeExtraLarge: {
            padding: '4px 14px',
            height: '56px',
            maxHeight: '56px',
            minWidth: '56px',
            fontSize: '16px'
          },
          sizeInput: {
            padding: '4px 14px',
            height: '48px',
            maxHeight: '48px',
            minWidth: '48px',
            fontSize: '14px'
          }
        }
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontSize: '14px'
          }
        }
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            fontSize: '11px',
            '& .MuiTypography-root': {
              color: '#9c9c9c',
              lineHeight: 1
            }
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        }
      },
      MuiDatePicker: {
        defaultProps: {
          OpenPickerButtonProps: {
            component: props => <IconButton {...props}>
                <img src={calendarIcon} />
              </IconButton>
          }
        }
      },
      InputDateRange: {
        iconButtonSize: 'large'
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 4,
            backgroundColor: '#273240',
            fontSize: '12px',
            padding: 12,
            maxHeight: '400px',
            overflowY: 'auto',
            '::-webkit-scrollbar': {
              width: '4px',
              borderRadius: 10,
              padding: 1
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: '#9C9C9C',
              borderRadius: 10,
              ':hover': {
                background: '#C5C5C5'
              }
            },
            '::-webkit-scrollbar-track': {},
            '::-webkit-scrollbar-button': {
              display: 'none'
            }
          }
        },
        defaultProps: {
          enterTouchDelay: 0,
          PopperProps: {
            popperOptions: {
              placement: 'auto'
            }
          }
        }
      }
    }
  });
}
export default create({});